<script setup lang="ts">
import { computed } from 'vue'
import type { ModelRef } from 'vue'

import { CPLoader } from '~/portal/components/base'
import { CPTypography } from '~/portal/components/layout'

const props = defineProps<{
	modelValue: boolean
	title: string
	message: string
	loading?: boolean
	progress?: {
		label: string
		percent: number
	}
}>()

const model: ModelRef<boolean> = defineModel<boolean>({ required: true })

const messageText = computed(() => props.message.replace(/[.…]+$/, '').trim())
</script>

<script lang="ts">
export default { inheritAttrs: false }
</script>

<template>
	<VOverlay
		v-model="model"
		:class="['cp-app-overlay', $attrs.class]"
		:persistent="true"
		:z-index="2400"
	>
		<div class="cp-app-overlay__card">
			<CPLoader v-if="loading" class="cp-app-overlay__spinner" />

			<CPTypography variant="h3" class="cp-app-overlay__title">
				{{ title }}
			</CPTypography>

			<CPTypography variant="body2" class="cp-app-overlay__message">
				{{ messageText }}<span
					class="cp-app-overlay__dots"
					aria-hidden="true"
				><span>.</span><span>.</span><span>.</span></span>
			</CPTypography>

			<div v-if="progress" class="cp-app-overlay__progress">
				<div class="cp-app-overlay__progress-head">
					<CPTypography
						variant="body2"
						class="cp-app-overlay__progress-name"
					>
						{{ progress.label }}
					</CPTypography>
					<CPTypography
						variant="body2"
						class="cp-app-overlay__progress-percent"
					>
						{{ progress.percent }}%
					</CPTypography>
				</div>
				<div class="cp-app-overlay__progress-track">
					<div
						class="cp-app-overlay__progress-fill"
						:style="{ width: `${progress.percent}%` }"
					/>
				</div>
			</div>
		</div>
	</VOverlay>
</template>

<style lang="scss">
.cp-app-overlay {
	z-index: 2400 !important;

	.v-overlay__scrim {
		background: #19191f;

		@apply opacity-50;
	}

	.v-overlay__content {
		@apply
			fixed
			inset-0
			w-full
			h-full
			flex
			items-center
			justify-center
		;
	}

	&__card {
		@apply
			flex
			flex-col
			items-center
			text-center
			bg-white
			rounded
			gap-5
			px-14
			py-12
			mx-4
			w-full
			max-w-[560px]
		;

		box-shadow: 0 4px 12px 0 #00000014;
	}

	&__title {
		@apply color-cp-neutral-700;
	}

	&__message {
		@apply color-cp-neutral-500;
	}

	&__progress {
		@apply flex flex-col gap-2 w-full;

		&-head {
			@apply flex items-center justify-between gap-4;
		}

		&-name {
			@apply color-cp-neutral-700 truncate;
		}

		&-percent {
			@apply color-cp-neutral-500 shrink-0;
		}

		&-track {
			@apply w-full h-2 rounded-full bg-cp-neutral-100 overflow-hidden;
		}

		&-fill {
			@apply h-full rounded-full bg-cp-main-300;

			transition: width 0.15s linear;
		}
	}
}
</style>
